// FILE: src/pages/Billing/BillingPage.jsx
// Full billing management — invoices list, create, status updates, summary stats
import React, { useEffect, useState, useCallback } from 'react';
import { billingAPI, clientsAPI, servicesAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './Billing.css';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}) : '—';
const fmtMoney = (n) =>
  `$${parseFloat(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;

const STATUS_STYLES = {
  'Pending':  'badge-warning',
  'Submitted':'badge-info',
  'Paid':     'badge-success',
  'Denied':   'badge-danger',
  'Void':     'badge-gray',
};

const EMPTY = {
  ClientID:'', ServiceID:'', ServiceDate:'', TotalHours:'',
  BillingRate:'', TotalAmount:'', BillingStatus:'Pending',
  InsuranceClaim:'', Notes:'',
};

export default function BillingPage() {
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin','Billing');

  const [records,   setRecords]   = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [summary,   setSummary]   = useState(null);
  const [clients,   setClients]   = useState([]);
  const [services,  setServices]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [statFilt,  setStatFilt]  = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [deleting,  setDeleting]  = useState(null);
  const [delLoad,   setDelLoad]   = useState(false);
  const [statModal, setStatModal] = useState(null); // quick status update

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [bRes, sumRes, cRes, sRes] = await Promise.all([
        billingAPI.getAll(),
        billingAPI.getSummary(),
        clientsAPI.getAll(),
        servicesAPI.getAll(),
      ]);
      setRecords(bRes.data.data   || []);
      setFiltered(bRes.data.data  || []);
      setSummary(sumRes.data.data || null);
      setClients(cRes.data.data   || []);
      setServices(sRes.data.data  || []);
    } catch { toast.error('Failed to load billing.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let data = [...records];
    if (statFilt) data = data.filter(r => r.BillingStatus === statFilt);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(r =>
        (r.ClientName||'').toLowerCase().includes(q) ||
        (r.ServiceName||'').toLowerCase().includes(q) ||
        (r.InsuranceClaim||'').toLowerCase().includes(q)
      );
    }
    setFiltered(data);
  }, [search, statFilt, records]);

  const handleDelete = async () => {
    if (!deleting) return;
    setDelLoad(true);
    try {
      await billingAPI.delete(deleting.ID);
      toast.success('Record deleted.');
      setDeleting(null);
      load();
    } catch { toast.error('Delete failed.'); }
    finally   { setDelLoad(false); }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await billingAPI.updateStatus(id, { BillingStatus: newStatus });
      toast.success(`Status updated to ${newStatus}.`);
      setStatModal(null);
      load();
    } catch { toast.error('Status update failed.'); }
  };

  const s = summary || {};

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Billing</h1>
          <p>{records.length} invoices total</p>
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search client, service, claim #…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:220}}/>
          </div>
          <select className="form-control" style={{width:160}} value={statFilt}
            onChange={e => setStatFilt(e.target.value)}>
            <option value="">All Statuses</option>
            {['Pending','Submitted','Paid','Denied','Void'].map(s=>
              <option key={s} value={s}>{s}</option>
            )}
          </select>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
              ➕ New Invoice
            </button>
          )}
        </div>
      </div>

      {/* Summary stats */}
      <div className="stats-grid-4" style={{marginBottom:20}}>
        {[
          {label:'Total Billed',    value:fmtMoney(s.totalBilled),   icon:'💰', color:'var(--primary)'},
          {label:'Total Paid',      value:fmtMoney(s.totalPaid),     icon:'✅', color:'var(--success)'},
          {label:'Pending',         value:fmtMoney(s.totalPending),  icon:'⏳', color:'var(--warning)'},
          {label:'Denied',          value:fmtMoney(s.totalDenied),   icon:'❌', color:'var(--danger)'},
        ].map(st => (
          <div className="stat-card billing-stat" key={st.label}
            style={{borderTop:`3px solid ${st.color}`}}>
            <div className="stat-card-header">
              <span className="stat-card-label">{st.label}</span>
              <span style={{fontSize:20}}>{st.icon}</span>
            </div>
            <div className="stat-card-value" style={{color:st.color}}>{st.value}</div>
          </div>
        ))}
      </div>

      {/* Invoice count chips */}
      <div className="billing-status-chips">
        {[
          {label:'All',       count:records.length,                             val:''},
          {label:'Pending',   count:records.filter(r=>r.BillingStatus==='Pending').length,   val:'Pending'},
          {label:'Submitted', count:records.filter(r=>r.BillingStatus==='Submitted').length, val:'Submitted'},
          {label:'Paid',      count:records.filter(r=>r.BillingStatus==='Paid').length,      val:'Paid'},
          {label:'Denied',    count:records.filter(r=>r.BillingStatus==='Denied').length,    val:'Denied'},
        ].map(c => (
          <button key={c.label}
            className={`billing-chip${statFilt===c.val?' active':''}`}
            onClick={() => setStatFilt(c.val)}>
            {c.label} <span className="chip-count">{c.count}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="card" style={{padding:0}}>
        {loading ? (
          <div className="loading-state">⏳ Loading billing records…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💰</div>
            <div className="empty-state-text">{search||statFilt ? 'No records match.' : 'No billing records yet.'}</div>
            {!search && !statFilt && canEdit && (
              <button className="btn btn-primary" style={{marginTop:14}}
                onClick={() => { setEditing(null); setShowModal(true); }}>
                ➕ Create First Invoice
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="ec-table">
              <thead>
                <tr>
                  <th>Client</th><th>Service</th><th>Service Date</th>
                  <th>Hours</th><th>Rate</th><th>Amount</th>
                  <th>Claim #</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.ID}>
                    <td className="bold">{r.ClientName||'—'}</td>
                    <td style={{fontSize:'0.82rem',color:'var(--gray-600)'}}>{r.ServiceName||'—'}</td>
                    <td style={{fontSize:'0.82rem',color:'var(--gray-500)'}}>{fmtDate(r.ServiceDate)}</td>
                    <td style={{fontSize:'0.82rem'}}>{r.TotalHours ? `${parseFloat(r.TotalHours).toFixed(2)}h` : '—'}</td>
                    <td style={{fontSize:'0.82rem'}}>{r.BillingRate ? `$${parseFloat(r.BillingRate).toFixed(2)}` : '—'}</td>
                    <td style={{fontWeight:700,color:'var(--gray-800)'}}>{fmtMoney(r.TotalAmount)}</td>
                    <td style={{fontSize:'0.78rem',color:'var(--gray-400)'}}>{r.InsuranceClaim||'—'}</td>
                    <td>
                      <span
                        className={`badge ${STATUS_STYLES[r.BillingStatus]||'badge-gray'}`}
                        style={{cursor:canEdit?'pointer':'default'}}
                        title={canEdit?'Click to change status':''}
                        onClick={() => canEdit && setStatModal(r)}>
                        {r.BillingStatus}
                        {canEdit && ' ▾'}
                      </span>
                    </td>
                    <td>
                      <div style={{display:'flex',gap:6}}>
                        {canEdit && (
                          <>
                            <button className="btn btn-sm btn-secondary" title="Edit"
                              onClick={() => { setEditing(r); setShowModal(true); }}>✏️</button>
                            <button className="btn btn-sm btn-danger" title="Delete"
                              onClick={() => setDeleting(r)}>🗑️</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      {showModal && (
        <BillingFormModal
          record={editing} clients={clients} services={services}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}

      {/* Quick status modal */}
      {statModal && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">Update Status</span>
              <button className="modal-close" onClick={() => setStatModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{color:'var(--gray-600)',marginBottom:14}}>
                Change status for <strong>{statModal.ClientName}</strong> — {fmtMoney(statModal.TotalAmount)}
              </p>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {['Pending','Submitted','Paid','Denied','Void'].map(st => (
                  <button key={st}
                    className={`btn ${statModal.BillingStatus===st?'btn-primary':'btn-secondary'}`}
                    onClick={() => handleStatusUpdate(statModal.ID, st)}>
                    {st}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleting && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">🗑️ Delete Invoice</span>
              <button className="modal-close" onClick={() => setDeleting(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{color:'var(--gray-600)'}}>
                Delete invoice for <strong>{deleting.ClientName}</strong> ({fmtMoney(deleting.TotalAmount)})?
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleting(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={delLoad}>
                {delLoad ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Billing Form Modal ────────────────────────────────────
function BillingFormModal({ record, clients, services, onClose, onSaved }) {
  const isEdit = !!record;
  const [form,   setForm]   = useState({
    ClientID:       record?.ClientID        || '',
    ServiceID:      record?.ServiceID       || '',
    ServiceDate:    record?.ServiceDate     ? record.ServiceDate.split('T')[0] : '',
    TotalHours:     record?.TotalHours      || '',
    BillingRate:    record?.BillingRate     || '',
    TotalAmount:    record?.TotalAmount     || '',
    BillingStatus:  record?.BillingStatus   || 'Pending',
    InsuranceClaim: record?.InsuranceClaim  || '',
    Notes:          record?.Notes           || '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (f,v) => {
    setForm(p => {
      const updated = {...p, [f]:v};
      // Auto-calculate total
      if (f === 'TotalHours' || f === 'BillingRate') {
        const h = parseFloat(f==='TotalHours' ? v : p.TotalHours) || 0;
        const r = parseFloat(f==='BillingRate' ? v : p.BillingRate) || 0;
        if (h && r) updated.TotalAmount = (h * r).toFixed(2);
      }
      return updated;
    });
    if (errors[f]) setErrors(p=>({...p,[f]:''}));
  };

  const validate = () => {
    const e = {};
    if (!form.ClientID)    e.ClientID    = 'Required';
    if (!form.ServiceDate) e.ServiceDate = 'Required';
    if (!form.TotalAmount) e.TotalAmount = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await billingAPI.update(record.ID, form);
        toast.success('Invoice updated.');
      } else {
        await billingAPI.create(form);
        toast.success('Invoice created.');
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed.'); }
    finally        { setSaving(false); }
  };

  const Err = ({f}) => errors[f] ? <div style={{color:'var(--danger)',fontSize:'0.75rem',marginTop:3}}>{errors[f]}</div> : null;

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <span className="modal-title">{isEdit ? '✏️ Edit Invoice' : '💰 New Invoice'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Client <span className="req">*</span></label>
              <select className={`form-control${errors.ClientID?' input-error':''}`}
                value={form.ClientID} onChange={e=>set('ClientID',e.target.value)}>
                <option value="">— Select Client —</option>
                {clients.map(c=><option key={c.ID} value={c.ID}>{c.FirstName} {c.LastName}</option>)}
              </select>
              <Err f="ClientID"/>
            </div>
            <div className="form-group">
              <label className="form-label">Service</label>
              <select className="form-control" value={form.ServiceID} onChange={e=>set('ServiceID',e.target.value)}>
                <option value="">— Select Service —</option>
                {services.map(s=><option key={s.ID} value={s.ID}>{s.ServiceName} (${s.BillingRate}/hr)</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Service Date <span className="req">*</span></label>
              <input className={`form-control${errors.ServiceDate?' input-error':''}`}
                type="date" value={form.ServiceDate} onChange={e=>set('ServiceDate',e.target.value)}/>
              <Err f="ServiceDate"/>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.BillingStatus} onChange={e=>set('BillingStatus',e.target.value)}>
                {['Pending','Submitted','Paid','Denied','Void'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Total Hours</label>
              <input className="form-control" type="number" step="0.25"
                value={form.TotalHours} onChange={e=>set('TotalHours',e.target.value)} placeholder="0.00"/>
            </div>
            <div className="form-group">
              <label className="form-label">Billing Rate ($/hr)</label>
              <input className="form-control" type="number" step="0.01"
                value={form.BillingRate} onChange={e=>set('BillingRate',e.target.value)} placeholder="0.00"/>
            </div>
            <div className="form-group">
              <label className="form-label">Total Amount <span className="req">*</span></label>
              <input className={`form-control${errors.TotalAmount?' input-error':''}`}
                type="number" step="0.01"
                value={form.TotalAmount} onChange={e=>set('TotalAmount',e.target.value)} placeholder="0.00"/>
              <Err f="TotalAmount"/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Insurance Claim #</label>
              <input className="form-control" value={form.InsuranceClaim}
                onChange={e=>set('InsuranceClaim',e.target.value)} placeholder="Claim number"/>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <input className="form-control" value={form.Notes}
                onChange={e=>set('Notes',e.target.value)} placeholder="Optional notes"/>
            </div>
          </div>

          {form.TotalHours && form.BillingRate && (
            <div style={{
              background:'var(--primary-pale)',border:'1px solid rgba(26,77,140,0.15)',
              borderRadius:'var(--radius-md)',padding:'12px 16px',marginTop:4,
              display:'flex',justifyContent:'space-between',alignItems:'center'
            }}>
              <span style={{fontSize:'0.85rem',color:'var(--primary)',fontWeight:600}}>
                📊 Auto-calculated: {form.TotalHours}h × ${form.BillingRate}/hr
              </span>
              <span style={{fontSize:'1.1rem',fontWeight:700,color:'var(--primary)'}}>
                = {form.TotalAmount ? `$${parseFloat(form.TotalAmount).toFixed(2)}` : '—'}
              </span>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? '💾 Update Invoice' : '💰 Create Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}